import { Controller } from "@hotwired/stimulus";
import { storageManager } from "../db/storageManager.js";
import { DebugLogger } from "../db/debugLogger.js";

const LOGGED_STATE_KEY = "artemis_storage_status_state";
const REFRESH_INTERVAL_MS = 30000;

// Warns when the device is running out of storage and offline route data is
// at risk. Rendered by StorageStatusComponent on the route launcher and every
// capture page. Capture pages are full page loads per sample; long-lived
// pages (the launcher) re-check on an interval so a filling disk surfaces —
// and a recovered one clears — without a reload.
export default class extends Controller {
  static targets = ["banner", "title", "message", "meter", "meterLabel"];

  static values = {
    warningTitle: String,
    warningMessage: String,
    criticalTitle: String,
    criticalMessage: String,
  };

  async connect() {
    await this.refresh();
    this.refreshInterval = setInterval(() => this.refresh(), REFRESH_INTERVAL_MS);
  }

  disconnect() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  async refresh() {
    try {
      this.render(await storageManager.getStorageStatus());
    } catch (error) {
      console.error("Error checking storage status:", error);
      this.hideBanner();
    }
  }

  render(storageStatus) {
    const percent = this.clampPercent(storageStatus?.usagePercent);

    if (storageStatus?.status === "critical") {
      this.logStateTransition(`critical:${percent}`, { percent });
      this.showBanner("critical", {
        title: this.criticalTitleValue,
        message: this.criticalMessageValue,
        percent,
      });
    } else if (storageStatus?.status === "warning") {
      this.logStateTransition(`warning:${percent}`, { percent });
      this.showBanner("warning", {
        title: this.warningTitleValue,
        message: this.warningMessageValue,
        percent,
      });
    } else {
      this.hideBanner();
    }
  }

  // Log only on state change — capture pages reload per sample, and an
  // unconditional warn would rewrite the debug-log blob thousands of times
  // per route on exactly the storage-stressed devices.
  logStateTransition(state, context) {
    try {
      if (sessionStorage.getItem(LOGGED_STATE_KEY) === state) return;
      sessionStorage.setItem(LOGGED_STATE_KEY, state);
    } catch {
      return;
    }
    DebugLogger.warn("Device storage pressure", { state, ...context });
  }

  showBanner(level, { title, message, percent }) {
    if (!this.hasBannerTarget) return;

    this.bannerTarget.classList.remove("hidden", "alert-warning", "alert-error");
    this.bannerTarget.classList.add(
      level === "critical" ? "alert-error" : "alert-warning",
    );

    if (this.hasTitleTarget) this.titleTarget.textContent = title;
    if (this.hasMessageTarget) this.messageTarget.textContent = message;

    if (this.hasMeterTarget) {
      this.meterTarget.style.setProperty("--value", percent);
      this.meterTarget.setAttribute("aria-valuenow", percent);
    }
    if (this.hasMeterLabelTarget) {
      this.meterLabelTarget.textContent = `${percent}%`;
    }
  }

  hideBanner() {
    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden");
  }

  // The radial meter renders 0–100; a failed write (quota signal) or a
  // simulated tiny quota can report percentages far beyond that.
  clampPercent(percent) {
    const rounded = Math.round(percent ?? 100);
    return Math.min(100, Math.max(0, Number.isFinite(rounded) ? rounded : 100));
  }
}
